import { color, font, testColors } from "@mesketch/design-tokens";
import { smoothPath, waterY, type Point } from "./sketch.js";

// Hladina, obraz výsledku testu emocí (CR-014 §3, zadání 8.5.2): body škál rovnoměrně v pořadí
// definice, výška podle polohy `z` na škále (nahoru kladný pól), hladká křivka s výplní, klidná
// hladina čárkovaně. Výrazné body s popiskem pólu, vyrovnané malé grafitové. Texty dodá volající.

export interface WaterPoint {
  readonly id: string;
  /** `null`: škála není spočítaná, bod chybí a křivka vede přes ostatní. */
  readonly z: number | null;
  readonly prominent: boolean;
  /** Popisek u výrazného bodu (slovo pólu). */
  readonly poleLabel: string | null;
  /** Krátký název škály pod obrazem (jen desktop). */
  readonly shortName: string;
}

export interface WaterProps {
  readonly points: readonly WaterPoint[];
  readonly variant: "desktop" | "mobile";
  readonly calmLabel: string;
  /** Textový ekvivalent celého obrazu (`aria-label`). */
  readonly label: string;
}

/**
 * Popisky sousedních bodů na téže straně hladiny se střídají ve výšce, aby se nepřekrývaly
 * (každý druhý o řádek dál od bodu).
 */
function stagger(drawn: readonly { y: number; p: WaterPoint }[], i: number, mid: number): number {
  let run = 0;
  for (let k = i; k > 0; k--) {
    const a = drawn[k];
    const b = drawn[k - 1];
    if (a === undefined || b === undefined || b.p.poleLabel === null || a.y < mid !== b.y < mid) break;
    run++;
  }
  return run % 2 === 1 ? 20 : 0;
}

/** Popisek nad bodem nad hladinou, pod bodem pod ní; u spodního okraje raději nad bodem. */
function labelY(y: number, mid: number, chart: number, shift: number): number {
  if (y < mid) return Math.max(14, y - 14 - shift);
  const below = y + 24 + shift;
  return below > chart - 4 ? y - 14 - shift : below;
}

const SIZE = { desktop: { w: 480, h: 230, names: 34 }, mobile: { w: 358, h: 190, names: 0 } } as const;
const EMOTION = testColors.emotion;

export function Water({ points, variant, calmLabel, label }: WaterProps) {
  const { w, h, names } = SIZE[variant];
  const chart = h - names;
  const mid = chart / 2;
  const amplitude = chart / 2 - 26;
  const step = w / points.length;
  const xs = points.map((_, i) => step * (i + 0.5));
  const drawn: (Point & { p: WaterPoint })[] = points.flatMap((p, i) => (p.z === null ? [] : [{ x: xs[i] ?? 0, y: waterY(p.z, mid, amplitude), p }]));
  if (drawn.length < 2) return null;
  const line = smoothPath(drawn, w);
  const area = `${line} L${w} ${chart} L0 ${chart} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label={label} data-testid="water" style={{ display: "block" }}>
      <path d={area} fill={EMOTION.bg} />
      <line x1="0" x2={w} y1={mid} y2={mid} stroke={color.graphite} strokeWidth="1" strokeDasharray="4 4" opacity={0.7} />
      <text x={w - 6} y={mid + 14} textAnchor="end" fontSize="11" fill={color.graphite} fontFamily={font.text}>
        {calmLabel}
      </text>
      <path className="water__line" d={line} fill="none" stroke={EMOTION.main} strokeWidth="2.4" strokeLinecap="round" pathLength={1} />
      <path d={line} transform="translate(0.8 1.2)" fill="none" stroke={color.graphite} strokeWidth="0.8" opacity={0.55} strokeLinecap="round" />
      {drawn.map(({ x, y, p }, i) =>
        p.prominent ? (
          <g key={p.id} data-point={p.id} data-prominent="true">
            <circle cx={x} cy={y} r="6.5" fill={EMOTION.strong} stroke={color.paper} strokeWidth="2.5" paintOrder="stroke" />
            {p.poleLabel === null ? null : (
              <text
                x={x}
                y={labelY(y, mid, chart, stagger(drawn, i, mid))}
                textAnchor={x < 48 ? "start" : x > w - 48 ? "end" : "middle"}
                dx={x < 48 ? -6 : x > w - 48 ? 6 : 0}
                fontSize={variant === "desktop" ? 17 : 15}
                fontWeight="700"
                fill={color.ink}
                fontFamily={font.display}
              >
                {p.poleLabel}
              </text>
            )}
          </g>
        ) : (
          <circle key={p.id} data-point={p.id} cx={x} cy={y} r="3.8" fill={color.graphite} stroke={color.paper} strokeWidth="1.5" paintOrder="stroke" />
        ),
      )}
      {names > 0
        ? points.map((p, i) => (
            <text key={p.id} x={xs[i]} y={chart + 20} textAnchor="middle" fontSize="11" fontWeight={p.prominent ? 700 : 400} fill={p.prominent ? color.ink : color.graphite} fontFamily={font.text}>
              {p.shortName}
            </text>
          ))
        : null}
    </svg>
  );
}
